import b4a from 'b4a'
import { domainHash, sign, normalizeUsername, isValidUsername } from '@pine/crypto'
import { botCardSignable } from './bots.js'

/**
 * Client for the username registry.
 *
 * The registry is the only server Pine talks to for state, and it exists for
 * one reason: a name announced only on the DHT cannot be made exclusive. Two
 * people could claim `@alex` and a searcher would have to choose, which is not
 * what a handle means to anyone.
 *
 * What crosses the wire is a statement signed by the account key. The server
 * verifies it and stores a mapping; it never holds a secret and cannot speak
 * for an account. If it is unreachable, or if someone runs Pine without one,
 * handles fall back to the DHT announcement — resolution still works, only
 * exclusivity is lost.
 */

const DEFAULT_TIMEOUT = 8000

/** Must stay byte-identical to claimSignable() in server/relay/lib/usernames.js. */
export function claimSignable ({ action, username, account, at }) {
  return domainHash('pine/username/claim/v1', action, username, account, String(at))
}

/**
 * What an account signs to change which of its badges are shown. Must stay
 * byte-identical to badgesSignable() in server/relay/lib/usernames.js.
 *
 * `hidden: null` is a read rather than a write, and signs differently so the
 * one cannot be turned into the other in flight.
 */
/**
 * What an account signs to take a collectible name. Must stay byte-identical
 * to collectSignable() in server/relay/lib/usernames.js.
 */
export function collectSignable ({ token, account, at }) {
  return domainHash('pine/username/collect/v1', String(token), String(account), String(at))
}

/** The token in a collectible-name link: /c/<token> or pine://collect/<token>. */
export function parseCollectLink (input) {
  const match = String(input || '').match(/(?:\/c\/|pine:\/\/collect\/)([a-z0-9]{16,64})/i)
  return match ? match[1].toLowerCase() : ''
}

/** The order badges are listed in, everywhere. */
export const BADGE_ORDER = ['verified', 'tester', 'donor', 'translator']

export function badgesSignable ({ account, at, hidden }) {
  const mode = hidden === null || hidden === undefined ? 'read' : 'set'
  return domainHash('pine/badges/visible/v1', account, String(at), mode, (hidden || []).join(','))
}

export class UsernameRegistryClient {
  constructor ({ url, identity }) {
    this.url = (url || '').replace(/\/+$/, '')
    this.identity = identity
  }

  get enabled () {
    return !!this.url
  }

  _sign (action, username) {
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(claimSignable({ action, username, account, at }), this.identity.secretKey)
    return { username, account, at, signature: b4a.toString(signature, 'hex') }
  }

  async _fetch (path, options = {}, timeout = DEFAULT_TIMEOUT) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeout)
    try {
      const response = await fetch(this.url + path, { ...options, signal: controller.signal })
      const body = await response.json().catch(() => ({}))
      return { status: response.status, body }
    } finally {
      clearTimeout(timer)
    }
  }

  /**
   * @throws Error with `.reason` of 'taken' when someone else holds the name,
   *         or 'offline' when the registry could not be reached.
   */
  async claim (input) {
    const username = normalizeUsername(input)
    if (!isValidUsername(username)) {
      const error = new Error('Invalid username')
      error.reason = 'charset'
      throw error
    }
    if (!this.enabled) return { username, authoritative: false }

    let result
    try {
      result = await this._fetch('/api/username', {
        method: 'POST',
        // Deliberately not application/json. That content type makes the
        // request non-simple, so a browser sends an OPTIONS preflight first —
        // and the Android build is cross-origin (it runs at pine.localhost),
        // so every claim depended on a server new enough to answer OPTIONS.
        // Against an older one the preflight failed, the POST was never sent,
        // and the app reported the name server as down. text/plain is
        // CORS-safelisted; the body is the same JSON, and the server parses
        // the body without consulting the header.
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(this._sign('claim', username))
      })
    } catch {
      const error = new Error('Registry unreachable')
      error.reason = 'offline'
      throw error
    }

    if (result.status === 200) return { username, authoritative: true, renewed: !!result.body.renewed }

    const error = new Error('Could not claim ' + username)
    error.reason = result.body.error || 'failed'
    error.status = result.status
    throw error
  }

  /** What a collectible link is for: { username, redeemed }, or null. */
  async describeCollectible (token) {
    if (!this.enabled || !token) return null
    try {
      const result = await this._fetch('/api/username/collect?token=' + encodeURIComponent(token), {}, 6000)
      return result.status === 200 ? result.body : null
    } catch {
      return null
    }
  }

  /**
   * Take a collectible name for this account.
   * @throws Error with `.reason` of 'used', 'unknown', 'taken', 'frozen' or 'offline'
   */
  async collect (token) {
    if (!this.enabled) throw Object.assign(new Error('No name server'), { reason: 'offline' })
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(collectSignable({ token, account, at }), this.identity.secretKey)
    let result
    try {
      result = await this._fetch('/api/username/collect', {
        method: 'POST',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ token, account, at, signature: b4a.toString(signature, 'hex') })
      })
    } catch {
      throw Object.assign(new Error('Registry unreachable'), { reason: 'offline' })
    }
    if (result.status === 200) return result.body
    throw Object.assign(new Error('Could not take that name'), { reason: result.body.error || 'failed' })
  }

  async release (input) {
    const username = normalizeUsername(input)
    if (!username || !this.enabled) return false
    try {
      const result = await this._fetch('/api/username', {
        method: 'DELETE',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(this._sign('release', username))
      })
      return result.status === 200
    } catch {
      return false
    }
  }

  /** @returns { username, account, since } | null */
  async lookup (input) {
    const username = normalizeUsername(input)
    if (!isValidUsername(username) || !this.enabled) return null
    try {
      const result = await this._fetch('/api/username/' + encodeURIComponent(username), {}, 5000)
      return result.status === 200 ? result.body : null
    } catch {
      return null
    }
  }

  /** What name, if any, this account currently holds. */
  async reverse (account) {
    if (!this.enabled) return null
    try {
      const result = await this._fetch(
        '/api/username?account=' + encodeURIComponent(account || this.identity.id), {}, 5000
      )
      return result.status === 200 ? result.body : null
    } catch {
      return null
    }
  }

  /**
   * Whether the operator has verified this account.
   *
   * Public and unsigned: verification is the server saying "I vouch for this
   * one", so it is only as trustworthy as the server, exactly like the name
   * mapping beside it. Returns null when there is no registry to ask.
   */
  async verified (account) {
    if (!this.enabled || !account) return null
    try {
      const result = await this._fetch('/api/verified/' + encodeURIComponent(account), {}, 5000)
      return result.status === 200 && result.body.verified ? result.body : { verified: false }
    } catch {
      return null
    }
  }

  /**
   * Which of our own badges are shown, and which we have chosen to keep to
   * ourselves.
   *
   * @param hidden an array to set the choice, or null to only read it back.
   * @returns { granted, hidden, visible } — or null when the registry could
   *   not be reached, which is different from "you have no badges".
   */
  async badgeVisibility (hidden = null) {
    if (!this.enabled) return null

    // Sent — and signed — in the one order both sides agree on, so what the
    // server checks is byte for byte what was signed.
    if (Array.isArray(hidden)) {
      hidden = BADGE_ORDER.filter((badge) => hidden.includes(badge))
    }

    const at = Date.now()
    const account = this.identity.id
    const signature = sign(
      badgesSignable({ account, at, hidden }), this.identity.secretKey
    )

    try {
      const result = await this._fetch('/api/badges/visibility', {
        method: 'POST',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({
          account,
          at,
          hidden: hidden === null || hidden === undefined ? null : hidden,
          signature: b4a.toString(signature, 'hex')
        })
      }, 8000)
      if (result.status === 200) return result.body
      // Reached, and refused. Not the same thing as unreachable, and the
      // screen should not say it is.
      return { error: (result.body && result.body.error) || 'failed' }
    } catch {
      return null
    }
  }

  /**
   * Which of these accounts the operator has frozen.
   *
   * @returns an array (possibly empty), or null when the registry could not
   *   be asked — which callers must not read as "none of them".
   */
  async frozen (accounts) {
    const list = (Array.isArray(accounts) ? accounts : [accounts]).filter(Boolean)
    if (!this.enabled || !list.length) return null
    try {
      const result = await this._fetch('/api/frozen', {
        method: 'POST',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ accounts: list.slice(0, 200) })
      }, 6000)
      if (result.status !== 200) return null
      return Array.isArray(result.body.frozen) ? result.body.frozen : []
    } catch {
      return null
    }
  }

  /**
   * The badges a batch of accounts carries.
   *
   * Asked about many at once because the places that show them are lists —
   * a chat list, a member list, search results — and one request per row is
   * one request per row. Answers nothing but what the server was told by its
   * operator: a badge is the server vouching, exactly like the name mapping.
   *
   * @returns { account: string[] } — accounts with no badges are simply
   *   absent. null when there is no registry to ask, which is different from
   *   an empty answer and is what stops a network blip from erasing badges
   *   that are already on screen.
   */
  /** Publish (or, with null, withdraw) this account's bot card. See lib/bots.js. */
  async publishBot (card) {
    if (!this.enabled) return false
    const at = Date.now()
    const account = this.identity.id
    const signature = sign(botCardSignable({ account, at, card }), this.identity.secretKey)
    const { status, body } = await this._fetch('/api/bots/card', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ account, at, card, signature: b4a.toString(signature, 'hex') })
    })
    if (status !== 200) {
      const error = new Error('Could not publish the bot card')
      error.reason = (body && body.error) || 'failed'
      throw error
    }
    return true
  }

  /** Bot cards for these accounts: { account: card } for the ones that are bots. */
  async botCards (accounts) {
    if (!this.enabled || !accounts || !accounts.length) return {}
    const { status, body } = await this._fetch('/api/bots/cards', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ accounts: accounts.slice(0, 200) })
    })
    return status === 200 && body && body.cards ? body.cards : {}
  }

  async badges (accounts) {
    const list = (Array.isArray(accounts) ? accounts : [accounts]).filter(Boolean)
    if (!this.enabled || !list.length) return null
    try {
      const result = await this._fetch('/api/badges', {
        method: 'POST',
        // Safelisted so the Android build, which is cross-origin, does not
        // have to survive a preflight for something this small.
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ accounts: list.slice(0, 200) })
      }, 6000)
      if (result.status !== 200) return null
      return result.body && result.body.badges && typeof result.body.badges === 'object'
        ? result.body.badges
        : {}
    } catch {
      return null
    }
  }
}
